import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, ArrowUpRight } from 'lucide-react'

import { cn } from '@/lib/utils'

type PillButtonProps = {
    href: string
    children: ReactNode
    variant?: 'filled' | 'outline' | 'cream'
    arrow?: 'right' | 'diagonal' | 'none'
    size?: 'sm' | 'lg'
    external?: boolean
    className?: string
}

/**
 * The one button shape used across the whole site: a 50px pill with an optional
 * trailing arrow, in a filled, outlined or cream variant.
 */
export const PillButton: FC<PillButtonProps> = ({
    href,
    children,
    variant = 'filled',
    arrow = 'right',
    size = 'sm',
    external = false,
    className
}) => {
    const classes = cn(
        'group/pill label inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full border transition-all duration-500 ease-dentea active:scale-[0.98]',
        size === 'sm' ? 'py-2 pr-3 pl-4' : 'px-10 py-5',
        variant === 'filled' && 'border-ink bg-ink text-paper hover:bg-charcoal',
        variant === 'outline' && 'border-ink/40 bg-transparent text-ink hover:border-ink hover:bg-ink/5',
        variant === 'cream' && 'border-cream bg-cream text-ink hover:bg-paper',
        className
    )

    const icon =
        arrow === 'none' ? null : arrow === 'diagonal' ? (
            <ArrowUpRight
                aria-hidden='true'
                className='size-3.5 transition-transform duration-500 ease-dentea group-hover/pill:translate-x-0.5 group-hover/pill:-translate-y-0.5'
            />
        ) : (
            <ArrowRight
                aria-hidden='true'
                className='size-3.5 transition-transform duration-500 ease-dentea group-hover/pill:translate-x-1'
            />
        )

    if (external) {
        return (
            <a href={href} target='_blank' rel='noreferrer' className={classes}>
                {children}
                {icon}
            </a>
        )
    }

    return (
        <Link href={href} className={classes}>
            {children}
            {icon}
        </Link>
    )
}
